// ============================================================================
// COMPONENTE: NotificationBell.jsx
// Sino de notificações no header mobile: badge com total de pendentes.
// Escuta: documentos para aprovação, tarefas e boletos pendentes.
// ============================================================================

import React, { useEffect, useRef, useState } from 'react';
import { Bell, FileText, ClipboardList, CreditCard, X } from 'lucide-react';
import { usePortalContext } from '../../context/PortalContext';
import { usePortalNotifications } from '../../hooks/usePortalNotifications';

export default function NotificationBell({ onNavigate }) {
  const { crmClient } = usePortalContext();
  const clientId = String(crmClient?.id || '').trim();
  const { docs, tasks, invoices, total } = usePortalNotifications();

  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // ── Fechar ao clicar fora ─────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (!clientId) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className={`relative h-7 w-7 rounded border flex items-center justify-center shrink-0 transition-colors ${
          open ? 'bg-[#20243B] text-white border-[#20243B]' : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'
        }`}
        title="Notificações"
        aria-label="Notificações"
      >
        <Bell className="w-3.5 h-3.5" />
        {total > 0 && (
          <span className="absolute -top-1.5 -right-1.5 h-4 min-w-4 px-0.5 rounded-full bg-red-500 text-white text-[8px] font-bold flex items-center justify-center leading-none">
            {total > 9 ? '9+' : total}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-9 w-72 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-3 py-2.5 border-b border-gray-100 bg-[#20243B]">
            <span className="text-xs font-extrabold text-white tracking-wide">Pendências</span>
            {total > 0 && (
              <span className="h-5 min-w-5 px-1.5 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">
                {total}
              </span>
            )}
            <button type="button" onClick={() => setOpen(false)} className="ml-auto text-white/50 hover:text-white transition-colors">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          <div className="max-h-72 overflow-y-auto divide-y divide-gray-100">
            {total === 0 && (
              <div className="px-4 py-6 text-center">
                <Bell className="w-6 h-6 text-gray-300 mx-auto mb-2" />
                <p className="text-xs text-gray-400 font-medium">Nenhuma pendência!</p>
              </div>
            )}

            {/* Documentos */}
            {docs.map(d => (
              <button
                key={d.id}
                type="button"
                onClick={() => { setOpen(false); if (onNavigate) onNavigate('caso'); }}
                className="w-full flex items-start gap-2.5 px-3 py-2.5 hover:bg-gray-50 text-left transition-colors"
              >
                <div className="mt-0.5 h-6 w-6 rounded-md bg-blue-50 flex items-center justify-center shrink-0">
                  <FileText className="w-3 h-3 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-bold text-[#20243B] truncate">{d.title || d.fileName || 'Documento'}</p>
                  <p className="text-[10px] text-gray-400 mt-0.5">Aguardando sua aprovação</p>
                </div>
              </button>
            ))}

            {/* Tarefas */}
            {tasks.map(t => (
              <button
                key={t.id}
                type="button"
                onClick={() => { setOpen(false); if (onNavigate) onNavigate('dashboard'); }}
                className="w-full flex items-start gap-2.5 px-3 py-2.5 hover:bg-gray-50 text-left transition-colors"
              >
                <div className="mt-0.5 h-6 w-6 rounded-md bg-amber-50 flex items-center justify-center shrink-0">
                  <ClipboardList className="w-3 h-3 text-amber-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-bold text-[#20243B] truncate">{t.title || 'Tarefa'}</p>
                  <p className="text-[10px] text-gray-400 mt-0.5">Tarefa pendente do escritório</p>
                </div>
              </button>
            ))}

            {/* Faturas */}
            {invoices.map(inv => (
              <button
                key={inv.id}
                type="button"
                onClick={() => { setOpen(false); if (onNavigate) onNavigate('financeiro'); }}
                className="w-full flex items-start gap-2.5 px-3 py-2.5 hover:bg-gray-50 text-left transition-colors"
              >
                <div className="mt-0.5 h-6 w-6 rounded-md bg-red-50 flex items-center justify-center shrink-0">
                  <CreditCard className="w-3 h-3 text-red-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-bold text-[#20243B] truncate">{inv.description || inv.title || 'Fatura'}</p>
                  <p className="text-[10px] text-gray-400 mt-0.5">Pagamento pendente</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
